import { Clear, ModeEdit, SaveAs } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { finalize } from "rxjs";
import { accountInformationService } from "services/accountInformationService";
import { notifications$ } from "services/subjects";
import TextInput from "./forms/TextInput/TextInput";

type MailingAddressFormValues = {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zip?: string;
};

type MailingAddressFormProps = {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zip?: string;
  memberId?: string;
  readOnly?: boolean;
  initialLoading?: boolean;
  onUpdateSuccess?: () => void;
};

const MailingAddressForm: React.FC<MailingAddressFormProps> = (props) => {
  const [saveEnabled, setSaveEnabled] = useState(false);
  const [saving, setSaving] = useState(false);

  const { control, handleSubmit, reset } = useForm<MailingAddressFormValues>({
    defaultValues: {
      addressLine1: props.addressLine1 ?? "",
      addressLine2: props.addressLine2 ?? "",
      city: props.city ?? "",
      state: props.state ?? "",
      zip: props.zip ?? "",
    },
  });

  useEffect(() => {
    reset({
      addressLine1: props.addressLine1 ?? "",
      addressLine2: props.addressLine2 ?? "",
      city: props.city ?? "",
      state: props.state ?? "",
      zip: props.zip ?? "",
    });
    setSaveEnabled(false);
  }, [
    props.addressLine1,
    props.addressLine2,
    props.city,
    props.state,
    props.zip,
  ]);

  const handleSubmitForm = (data: MailingAddressFormValues) => {
    const finalForm = {
      addressLine1: data.addressLine1?.trim() || null,
      addressLine2: data.addressLine2?.trim() || null,
      city: data.city?.trim() || null,
      state: data.state?.trim() || null,
      zip: data.zip?.trim() || null,
      memberId: props.memberId ?? "",
    };

    setSaving(true);
    accountInformationService
      .updateMailingAddress$(finalForm)
      .pipe(finalize(() => setSaving(false)))
      .subscribe({
        next: () => {
          notifications$.next({
            severity: "success",
            message:
              "Done! - Your request has been submitted",
          });
          setSaveEnabled(false);
          if (props.onUpdateSuccess) {
            props.onUpdateSuccess();
          }
        },
        error: () => {
          notifications$.next({
            severity: "error",
            message:
              "An error occurred while saving your mailing address. Please try again.",
          });
        },
      });
  };

  const disabled = props.readOnly || !saveEnabled || saving;

  return (
    <Box mb={4}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 1,
          mb: 2,
        }}
      >
        <Typography variant="h4">Mailing Address</Typography>
        {saving ? (
          <CircularProgress size={24} />
        ) : saveEnabled ? (
          <Box>
            <Button
              variant="text"
              aria-label="save"
              onClick={handleSubmit(handleSubmitForm)}
            >
              <SaveAs fontSize="small" sx={{ mr: 1 }} color="primary" />
              <Typography color="primary" fontSize="small">
                Save changes
              </Typography>
            </Button>
            <Button
              variant="text"
              aria-label="cancel"
              onClick={() => setSaveEnabled(false)}
            >
              <Clear color="error" sx={{ mr: 1 }} />
              <Typography fontSize="small" color="error">
                Clear
              </Typography>
            </Button>
          </Box>
        ) : (
          <Button
            variant="text"
            disabled={props.readOnly}
            aria-label="update mailing address info"
            onClick={() => setSaveEnabled(true)}
          >
            <ModeEdit fontSize="small" sx={{ mr: 1 }} />
            <Typography fontSize="small">Edit</Typography>
          </Button>
        )}
      </Box>
      <Grid container spacing={2}  columns={{
          xs: 1,
          sm: 3,
          md: 5,
        }}>
        <Grid size={1}>
          <Controller
            name="addressLine1"
            control={control}
            rules={{
              required: "Address is required",
            }}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...field}
                label="Address 1"
                fullWidth
                disabled={disabled}
                initialLoading={props.initialLoading}
                error={!!error?.message}
                helperText={error?.message || ""}
              />
            )}
          />
        </Grid>
         <Grid size={1}>
          <Controller
            name="addressLine2"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...field}
                label="Address 2"
                fullWidth
                disabled={disabled}
                initialLoading={props.initialLoading}
                error={!!error?.message}
                helperText={error?.message || ""}
              />
            )}
          />
        </Grid>
         <Grid size={1}>
          <Controller
            name="city"
            rules={{
              required: "City is required",
            }}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...field}
                label="City"
                fullWidth
                disabled={disabled}
                initialLoading={props.initialLoading}
                error={!!error?.message}
                helperText={error?.message || ""}
              />
            )}
          />
        </Grid>
        <Grid size={1}>
          <Controller
            name="state"
            rules={{
              required: "State is required",
            }}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...field}
                label="State"
                fullWidth
                disabled={disabled}
                initialLoading={props.initialLoading}
                error={!!error?.message}
                helperText={error?.message || ""}

              />
            )}
          />
        </Grid>
         <Grid size={1}>
          <Controller
            name="zip"
            control={control}
            rules={{
              required: "Zip code is required",
            }}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...field}
                label="Zip Code"
                fullWidth
                disabled={disabled}
                initialLoading={props.initialLoading}
                error={!!error?.message}
                helperText={error?.message || ""}

              />
            )}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default MailingAddressForm;
